import type { ReactNode } from "react";
import type { CampaignSchema } from "@/types";

import { ArrowRightCircle } from "lucide-react";
import { CampaignImage } from "../campaign-image";
import { CampaignLink } from "@/components/campaign-link";
import { Typography } from "@/components/ui/typography";
import { campaignKey } from "@/campaigns/campaign-key";
import { useTranslation } from "react-i18next";
import { campaignDisplayTitle } from "@/utils/campaign-helpers";

/**
 * The shared grid card for finished campaigns: greyed image + a bottom block
 * with the title and the per-type result (won prize, ended date, …) passed as
 * children. The title sits below the image — same structure as the active
 * cards — never overlaid on the artwork.
 */
export const FinishedCampaignCard = ({
  campaign,
  children,
}: {
  campaign: CampaignSchema;
  children: ReactNode;
}) => {
  const { t } = useTranslation();
  const id = campaignKey(campaign);

  return (
    <CampaignLink campaignId={id}>
      <div className="flex h-full w-full snap-center flex-col overflow-hidden rounded-lg border border-disabled bg-secondary shadow-sm select-none">
        <CampaignImage darkenImage hideDuration hideTitle campaign={campaign} />
        <div className="flex w-full flex-1 flex-col gap-1 border-t border-border bg-secondary px-3 py-2.5">
          <Typography tag="h6" className="truncate leading-normal">
            {campaignDisplayTitle(campaign, t)}
          </Typography>
          <div className="flex w-full flex-row items-center justify-between gap-2">
            {children}
            <ArrowRightCircle
              strokeWidth={1}
              fill="currentColor"
              className="size-8 shrink-0 fill-background text-muted-foreground"
            />
          </div>
        </div>
      </div>
    </CampaignLink>
  );
};
